import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { api } from '@/services/api-class';
import moment from 'moment';

type SearchType = 'person' | 'vehicle' | 'business' | 'client' | 'callcard';

interface SearchFields {
  // Person fields
  firstName: string;
  middleName: string;
  lastName: string;
  dob: string;
  driversLicenseNum: string;
  address: string;
  // Vehicle fields
  plateNumber: string;
  plateState: string;
  vin: string;
  make: string;
  model: string;
  color: string;
  // Business fields
  businessName: string;
  businessBranchName: string;
  businessWebsite: string;
  businessAddress: string;
  // Client fields
  clientName: string;
  clientAddress: string;
  clientState: string;
  clientWebsite: string;
  // Call card fields
  eventId: string;
  callLocation: string;
  callerName: string;
  startDate: string;
  endDate: string;
}

function createEmptySearchFields(): SearchFields {
  return {
    firstName: '',
    middleName: '',
    lastName: '',
    dob: '',
    driversLicenseNum: '',
    address: '',
    plateNumber: '',
    plateState: '',
    vin: '',
    make: '',
    model: '',
    color: '',
    businessName: '',
    businessBranchName: '',
    businessWebsite: '',
    businessAddress: '',
    clientName: '',
    clientAddress: '',
    clientState: '',
    clientWebsite: '',
    eventId: '',
    callLocation: '',
    callerName: '',
    startDate: '',
    endDate: '',
  };
}

export interface SearchModalProps {
  open: boolean;
  searchType: SearchType;
  title?: string;
  theme?: string;
  initialValues?: Record<string, any>;
  onSelect?: (item: any) => void;
  onView?: (item: any) => void;
  onCancel: () => void;
}

export function SearchModal({
  open,
  searchType,
  title,
  theme: themeProp,
  initialValues,
  onSelect,
  onView,
  onCancel,
}: SearchModalProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;
  const [searchFields, setSearchFields] = useState<SearchFields>(createEmptySearchFields);
  const [results, setResults] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const dialogRef = useRef<HTMLDivElement>(null);

  // --- Computed / memoized values ---

  const isPersonSearch = searchType === 'person';
  const isVehicleSearch = searchType === 'vehicle';
  const isBusinessSearch = searchType === 'business';
  const isClientSearch = searchType === 'client';
  const isCallCardSearch = searchType === 'callcard';

  const canSelect = selectedItem !== null;

  const modalTitle = useMemo(() => {
    if (title) return title;
    if (isPersonSearch) return 'Search Person';
    if (isVehicleSearch) return 'Search Vehicle';
    if (isBusinessSearch) return 'Search Business';
    if (isClientSearch) return 'Search Client';
    if (isCallCardSearch) return 'Search Call Card';
    return 'Search';
  }, [title, isPersonSearch, isVehicleSearch, isBusinessSearch, isClientSearch, isCallCardSearch]);

  const totalPages = useMemo(() => {
    return Math.ceil(totalRecords / pageSize);
  }, [totalRecords, pageSize]);

  const isMobile = useMemo(() => {
    return windowWidth <= 768;
  }, [windowWidth]);

  const searchSummary = useMemo(() => {
    const parts: string[] = [];
    if (isPersonSearch) {
      if (searchFields.lastName) parts.push(searchFields.lastName);
      if (searchFields.firstName) parts.push(searchFields.firstName);
      if (searchFields.dob) {
        const d = moment(searchFields.dob, 'YYYY-MM-DD');
        if (d.isValid()) parts.push(d.format('MM/DD/YYYY'));
      }
      if (searchFields.driversLicenseNum) parts.push('DL: ' + searchFields.driversLicenseNum);
    } else if (isVehicleSearch) {
      if (searchFields.plateNumber) {
        let plate = searchFields.plateNumber;
        if (searchFields.plateState) plate += ' (' + searchFields.plateState + ')';
        parts.push(plate);
      }
      if (searchFields.vin) parts.push('VIN: ' + searchFields.vin);
      if (searchFields.make) parts.push(searchFields.make);
      if (searchFields.model) parts.push(searchFields.model);
    } else if (isBusinessSearch) {
      if (searchFields.businessName) parts.push(searchFields.businessName);
      if (searchFields.businessBranchName) parts.push(searchFields.businessBranchName);
    } else if (isClientSearch) {
      if (searchFields.clientName) parts.push(searchFields.clientName);
    } else if (isCallCardSearch) {
      if (searchFields.eventId) parts.push('Event: ' + searchFields.eventId);
      if (searchFields.callerName) parts.push(searchFields.callerName);
    }
    return parts.length > 0 ? parts.join(', ') : 'All records';
  }, [searchFields, isPersonSearch, isVehicleSearch, isBusinessSearch, isClientSearch, isCallCardSearch]);

  // --- Helpers ---

  const formatDate = useCallback((date: any): string => {
    if (!date) return '';
    const m = moment.utc(date);
    if (!m.isValid()) return '';
    return m.format('DD MMM YYYY');
  }, []);

  const formatDateTime = useCallback((date: any): string => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const formatNatureOfCall = useCallback((nocValues: any[]): string => {
    if (!nocValues || !Array.isArray(nocValues) || nocValues.length === 0) {
      return '';
    }
    return nocValues.map((noc: any) => noc.value || noc).join(', ');
  }, []);

  // --- Build search data ---

  const buildSearchData = useCallback((fields: SearchFields, page: number, ps: number) => {
    const filters: any[] = [];

    if (isPersonSearch) {
      if (fields.firstName) filters.push({ field: 'firstName', operator: 'contains', value: fields.firstName });
      if (fields.middleName) filters.push({ field: 'middleName', operator: 'contains', value: fields.middleName });
      if (fields.lastName) filters.push({ field: 'lastName', operator: 'contains', value: fields.lastName });
      if (fields.dob) {
        const dobMoment = moment.utc(fields.dob, 'YYYY-MM-DD');
        if (dobMoment.isValid()) {
          filters.push({ field: 'dob', operator: 'eq', value: dobMoment.format('DD MMM YYYY') });
        }
      }
      if (fields.driversLicenseNum) filters.push({ field: 'driversLicenseNum', operator: 'contains', value: fields.driversLicenseNum });
      if (fields.address) filters.push({ field: 'locFormattedAddress', operator: 'contains', value: fields.address });
    } else if (isVehicleSearch) {
      if (fields.plateNumber) filters.push({ field: 'plateNumber', operator: 'contains', value: fields.plateNumber });
      if (fields.plateState) filters.push({ field: 'plateState', operator: 'contains', value: fields.plateState });
      if (fields.vin) filters.push({ field: 'vin', operator: 'contains', value: fields.vin });
      if (fields.make) filters.push({ field: 'make', operator: 'contains', value: fields.make });
      if (fields.model) filters.push({ field: 'model', operator: 'contains', value: fields.model });
      if (fields.color) filters.push({ field: 'color', operator: 'contains', value: fields.color });
    } else if (isBusinessSearch) {
      if (fields.businessName) filters.push({ field: 'businessName', operator: 'contains', value: fields.businessName });
      if (fields.businessBranchName) filters.push({ field: 'branchName', operator: 'contains', value: fields.businessBranchName });
      if (fields.businessWebsite) filters.push({ field: 'website', operator: 'contains', value: fields.businessWebsite });
      if (fields.businessAddress) filters.push({ field: 'locFormattedAddress', operator: 'contains', value: fields.businessAddress });
    } else if (isClientSearch) {
      if (fields.clientName) filters.push({ field: 'name', operator: 'contains', value: fields.clientName });
      if (fields.clientAddress) filters.push({ field: 'locFormattedAddress', operator: 'contains', value: fields.clientAddress });
      if (fields.clientState) filters.push({ field: 'locState', operator: 'contains', value: fields.clientState });
      if (fields.clientWebsite) filters.push({ field: 'website', operator: 'contains', value: fields.clientWebsite });
    } else if (isCallCardSearch) {
      if (fields.eventId) filters.push({ field: 'eventId', operator: 'startswith', value: fields.eventId });
      if (fields.callLocation) filters.push({ field: 'callLocFormattedAddress', operator: 'contains', value: fields.callLocation });
      if (fields.callerName) filters.push({ field: 'callerName', operator: 'contains', value: fields.callerName });
      if (fields.startDate) {
        const startMoment = moment(fields.startDate);
        if (startMoment.isValid()) {
          filters.push({ field: 'callDateTime', operator: 'gte', value: startMoment.utc().format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]') });
        }
      }
      if (fields.endDate) {
        const endMoment = moment(fields.endDate).add(23, 'hours').add(59, 'minutes').add(59, 'seconds').add(999, 'milliseconds');
        if (endMoment.isValid()) {
          filters.push({ field: 'callDateTime', operator: 'lte', value: endMoment.utc().format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]') });
        }
      }
    }

    return {
      data: {
        take: ps,
        skip: (page - 1) * ps,
        page: page,
        pageSize: ps,
        filter: {
          logic: 'and',
          filters: filters,
        },
      },
    };
  }, [isPersonSearch, isVehicleSearch, isBusinessSearch, isClientSearch, isCallCardSearch]);

  // --- Perform search ---

  const performSearch = useCallback((fields?: SearchFields, page?: number) => {
    const f = fields || searchFields;
    const p = page || currentPage;

    setIsLoading(true);
    setSelectedItem(null);

    const searchData = buildSearchData(f, p, pageSize);

    if (isCallCardSearch) {
      api.callCardSearch(
        'all',
        p,
        pageSize.toString(),
        JSON.stringify(searchData),
        (result: any) => {
          setResults(result.entries || []);
          setTotalRecords(result.records || 0);
          setHasSearched(true);
          setIsLoading(false);
          if (windowWidth <= 768) {
            setFiltersCollapsed(true);
          }
        }
      );
    } else {
      let apiSearchType = searchType;
      if (isClientSearch) apiSearchType = 'client';
      else if (isBusinessSearch) apiSearchType = 'Business' as any;
      else if (isPersonSearch) apiSearchType = 'Person' as any;
      else if (isVehicleSearch) apiSearchType = 'Vehicle' as any;

      api.npsdSearch(
        apiSearchType,
        p,
        pageSize.toString(),
        JSON.stringify(searchData),
        (result: any) => {
          setResults(result.entries || []);
          setTotalRecords(result.records || 0);
          setHasSearched(true);
          setIsLoading(false);
          if (windowWidth <= 768) {
            setFiltersCollapsed(true);
          }
        }
      );
    }
  }, [searchFields, currentPage, pageSize, buildSearchData, isCallCardSearch, isClientSearch, isBusinessSearch, isPersonSearch, isVehicleSearch, searchType, windowWidth]);

  // --- Apply initial values ---

  const applyInitialValues = useCallback((fields: SearchFields): SearchFields => {
    const vals = initialValues || {};
    const updated = { ...fields };

    if (isPersonSearch) {
      if (vals.firstName) updated.firstName = vals.firstName;
      if (vals.middleName) updated.middleName = vals.middleName;
      if (vals.lastName) updated.lastName = vals.lastName;
      if (vals.dob) {
        const dobMoment = moment(vals.dob, ['MM/DD/YYYY', 'YYYY-MM-DD', 'DD MMM YYYY']);
        updated.dob = dobMoment.isValid() ? dobMoment.format('YYYY-MM-DD') : '';
      }
      if (vals.driversLicenseNum) updated.driversLicenseNum = vals.driversLicenseNum;
      if (vals.address) updated.address = vals.address;
    } else if (isVehicleSearch) {
      if (vals.plateNumber) updated.plateNumber = vals.plateNumber;
      if (vals.plateState) updated.plateState = vals.plateState;
      if (vals.vin) updated.vin = vals.vin;
      if (vals.make) updated.make = vals.make;
      if (vals.model) updated.model = vals.model;
      if (vals.color) updated.color = vals.color;
    } else if (isBusinessSearch) {
      if (vals.businessName || vals.name) updated.businessName = vals.businessName || vals.name;
      if (vals.businessBranchName || vals.branchName) updated.businessBranchName = vals.businessBranchName || vals.branchName;
      if (vals.businessWebsite || vals.website) updated.businessWebsite = vals.businessWebsite || vals.website;
      if (vals.businessAddress || vals.address) updated.businessAddress = vals.businessAddress || vals.address;
    } else if (isClientSearch) {
      if (vals.clientName || vals.name) updated.clientName = vals.clientName || vals.name;
      if (vals.clientAddress || vals.address) updated.clientAddress = vals.clientAddress || vals.address;
      if (vals.clientState || vals.state) updated.clientState = vals.clientState || vals.state;
      if (vals.clientWebsite || vals.website) updated.clientWebsite = vals.clientWebsite || vals.website;
    } else if (isCallCardSearch) {
      if (vals.eventId) updated.eventId = vals.eventId;
      if (vals.callLocation) updated.callLocation = vals.callLocation;
      if (vals.callerName) updated.callerName = vals.callerName;
      if (vals.startDate) updated.startDate = vals.startDate;
      if (vals.endDate) updated.endDate = vals.endDate;
    }

    return updated;
  }, [initialValues, isPersonSearch, isVehicleSearch, isBusinessSearch, isClientSearch, isCallCardSearch]);

  // --- Escape key listener ---

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onCancel]);

  // --- Window resize listener (debounced) ---

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handleResize = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setWindowWidth(window.innerWidth), 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // --- Scroll lock, reset, auto-search on open change ---

  useEffect(() => {
    if (open) {
      // Reset
      const emptyFields = createEmptySearchFields();
      const fieldsWithInitial = applyInitialValues(emptyFields);
      setSearchFields(fieldsWithInitial);
      setResults([]);
      setTotalRecords(0);
      setCurrentPage(1);
      setHasSearched(false);
      setSelectedItem(null);
      setIsLoading(false);
      setFiltersCollapsed(false);

      document.body.classList.add('kstc-scroll-lock');

      // Focus first input and auto-search after React renders
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Focus first input
          if (dialogRef.current) {
            const firstInput = dialogRef.current.querySelector('.ksts-form-fields .ksts-field input') as HTMLInputElement;
            if (firstInput) firstInput.focus();
          }
          // Auto-search on open
          setTimeout(() => {
            performSearchDirect(fieldsWithInitial, 1);
          }, 50);
        });
      });
    } else {
      document.body.classList.remove('kstc-scroll-lock');
    }

    return () => {
      document.body.classList.remove('kstc-scroll-lock');
    };
    // We intentionally only run on open change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Direct search that takes fields+page to avoid stale closure issues on open
  const performSearchDirect = useCallback((fields: SearchFields, page: number) => {
    setIsLoading(true);
    setSelectedItem(null);

    const searchData = buildSearchData(fields, page, pageSize);

    if (isCallCardSearch) {
      api.callCardSearch(
        'all',
        page,
        pageSize.toString(),
        JSON.stringify(searchData),
        (result: any) => {
          setResults(result.entries || []);
          setTotalRecords(result.records || 0);
          setHasSearched(true);
          setIsLoading(false);
          if (window.innerWidth <= 768) {
            setFiltersCollapsed(true);
          }
        }
      );
    } else {
      let apiSearchType = searchType;
      if (isClientSearch) apiSearchType = 'client';
      else if (isBusinessSearch) apiSearchType = 'Business' as any;
      else if (isPersonSearch) apiSearchType = 'Person' as any;
      else if (isVehicleSearch) apiSearchType = 'Vehicle' as any;

      api.npsdSearch(
        apiSearchType,
        page,
        pageSize.toString(),
        JSON.stringify(searchData),
        (result: any) => {
          setResults(result.entries || []);
          setTotalRecords(result.records || 0);
          setHasSearched(true);
          setIsLoading(false);
          if (window.innerWidth <= 768) {
            setFiltersCollapsed(true);
          }
        }
      );
    }
  }, [buildSearchData, pageSize, isCallCardSearch, isClientSearch, isBusinessSearch, isPersonSearch, isVehicleSearch, searchType]);

  // --- Event handlers ---

  const handleFieldChange = useCallback((field: keyof SearchFields, value: string) => {
    setSearchFields(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSearchClick = useCallback(() => {
    setCurrentPage(1);
    performSearch(searchFields, 1);
  }, [performSearch, searchFields]);

  const handleResetSearch = useCallback(() => {
    setSearchFields(createEmptySearchFields());
    setResults([]);
    setTotalRecords(0);
    setCurrentPage(1);
    setHasSearched(false);
    setSelectedItem(null);
    setIsLoading(false);
    setFiltersCollapsed(false);
  }, []);

  const handleKeyUpEnter = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setCurrentPage(1);
      performSearch(searchFields, 1);
    }
  }, [performSearch, searchFields]);

  const handleSelectItem = useCallback((item: any) => {
    setSelectedItem(item);
  }, []);

  const handleDoubleClickItem = useCallback((item: any) => {
    setSelectedItem(item);
    if (onSelect) onSelect(item);
  }, [onSelect]);

  const handleConfirmSelection = useCallback(() => {
    if (selectedItem && onSelect) {
      onSelect(selectedItem);
    }
  }, [selectedItem, onSelect]);

  const handleViewSelected = useCallback(() => {
    if (selectedItem && onView) {
      onView(selectedItem);
    }
  }, [selectedItem, onView]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).classList.contains('kstc-backdrop')) {
      onCancel();
    }
  }, [onCancel]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      const nextP = currentPage + 1;
      setCurrentPage(nextP);
      performSearch(searchFields, nextP);
    }
  }, [currentPage, totalPages, performSearch, searchFields]);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      const prevP = currentPage - 1;
      setCurrentPage(prevP);
      performSearch(searchFields, prevP);
    }
  }, [currentPage, performSearch, searchFields]);

  const handleToggleFilters = useCallback(() => {
    setFiltersCollapsed(prev => {
      const newVal = !prev;
      if (!newVal) {
        // Expanding: focus first input after render
        requestAnimationFrame(() => {
          if (dialogRef.current) {
            const firstInput = dialogRef.current.querySelector('.ksts-form-fields .ksts-field input') as HTMLInputElement;
            if (firstInput) firstInput.focus();
          }
        });
      }
      return newVal;
    });
  }, []);

  // --- Don't render if not open ---

  if (!open) return null;

  // --- Determine if filters should be shown ---

  const showFilters = !isMobile || !filtersCollapsed || !hasSearched;

  // --- Render ---

  return (
    <div
      data-kstc-modal
      className={`kstc-backdrop ksts-search-modal ${theme || ''}`}
      onClick={handleBackdropClick}
    >
      <div className="ksts-dialog" role="dialog" aria-modal="true" ref={dialogRef}>
        {/* Header */}
        <div className="kstc-header">
          <h2 className="kstc-title">{modalTitle}</h2>
          <button className="kstc-x" onClick={onCancel} type="button" aria-label="Close">&times;</button>
        </div>

        {/* Collapsed Filter Bar (mobile only) */}
        {isMobile && filtersCollapsed && hasSearched && (
          <div className="ksts-collapsed-filters">
            <div className="ksts-collapsed-summary">
              <span className="ksts-collapsed-label">Searching:</span>
              <span className="ksts-collapsed-text">{searchSummary}</span>
            </div>
            <button type="button" className="ksts-expand-btn" onClick={handleToggleFilters}>
              <i className="fa fa-filter"></i> Modify Search
            </button>
          </div>
        )}

        {/* Search Form (hidden when collapsed on mobile) */}
        <div className="ksts-search-form" style={showFilters ? undefined : { display: 'none' }}>
          {/* Form Fields Container (scrollable on mobile) */}
          <div className="ksts-form-fields">
            {/* Person Search Fields */}
            {isPersonSearch && (
              <>
                <div className="ksts-form-row">
                  <div className="ksts-field">
                    <label>First Name</label>
                    <input
                      type="text"
                      value={searchFields.firstName}
                      onChange={e => handleFieldChange('firstName', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="First name"
                    />
                  </div>
                  <div className="ksts-field">
                    <label>Middle Name</label>
                    <input
                      type="text"
                      value={searchFields.middleName}
                      onChange={e => handleFieldChange('middleName', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Middle name"
                    />
                  </div>
                  <div className="ksts-field">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={searchFields.lastName}
                      onChange={e => handleFieldChange('lastName', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Last name"
                    />
                  </div>
                </div>
                <div className="ksts-form-row">
                  <div className="ksts-field">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      value={searchFields.dob}
                      onChange={e => handleFieldChange('dob', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                    />
                  </div>
                  <div className="ksts-field">
                    <label>Driver's License #</label>
                    <input
                      type="text"
                      value={searchFields.driversLicenseNum}
                      onChange={e => handleFieldChange('driversLicenseNum', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="License number"
                    />
                  </div>
                </div>
                <div className="ksts-form-row">
                  <div className="ksts-field">
                    <label>Address</label>
                    <input
                      type="text"
                      value={searchFields.address}
                      onChange={e => handleFieldChange('address', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Address"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Vehicle Search Fields */}
            {isVehicleSearch && (
              <>
                <div className="ksts-form-row">
                  <div className="ksts-field">
                    <label>Plate Number</label>
                    <input
                      type="text"
                      value={searchFields.plateNumber}
                      onChange={e => handleFieldChange('plateNumber', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Plate number"
                    />
                  </div>
                  <div className="ksts-field ksts-field--small">
                    <label>Plate State</label>
                    <input
                      type="text"
                      value={searchFields.plateState}
                      maxLength={2}
                      onChange={e => handleFieldChange('plateState', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="ST"
                    />
                  </div>
                  <div className="ksts-field">
                    <label>VIN</label>
                    <input
                      type="text"
                      value={searchFields.vin}
                      maxLength={17}
                      onChange={e => handleFieldChange('vin', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Vehicle ID number"
                    />
                  </div>
                </div>
                <div className="ksts-form-row">
                  <div className="ksts-field">
                    <label>Make</label>
                    <input
                      type="text"
                      value={searchFields.make}
                      onChange={e => handleFieldChange('make', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Make"
                    />
                  </div>
                  <div className="ksts-field">
                    <label>Model</label>
                    <input
                      type="text"
                      value={searchFields.model}
                      onChange={e => handleFieldChange('model', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Model"
                    />
                  </div>
                  <div className="ksts-field">
                    <label>Color</label>
                    <input
                      type="text"
                      value={searchFields.color}
                      onChange={e => handleFieldChange('color', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Color"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Business Search Fields */}
            {isBusinessSearch && (
              <>
                <div className="ksts-form-row">
                  <div className="ksts-field">
                    <label>Business Name</label>
                    <input
                      type="text"
                      value={searchFields.businessName}
                      onChange={e => handleFieldChange('businessName', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Business name"
                    />
                  </div>
                  <div className="ksts-field">
                    <label>Branch Name</label>
                    <input
                      type="text"
                      value={searchFields.businessBranchName}
                      onChange={e => handleFieldChange('businessBranchName', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Branch name"
                    />
                  </div>
                </div>
                <div className="ksts-form-row">
                  <div className="ksts-field">
                    <label>Website</label>
                    <input
                      type="text"
                      value={searchFields.businessWebsite}
                      onChange={e => handleFieldChange('businessWebsite', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Website"
                    />
                  </div>
                  <div className="ksts-field">
                    <label>Address</label>
                    <input
                      type="text"
                      value={searchFields.businessAddress}
                      onChange={e => handleFieldChange('businessAddress', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Address"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Client Search Fields */}
            {isClientSearch && (
              <>
                <div className="ksts-form-row">
                  <div className="ksts-field">
                    <label>Client Name</label>
                    <input
                      type="text"
                      value={searchFields.clientName}
                      onChange={e => handleFieldChange('clientName', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Client name"
                    />
                  </div>
                  <div className="ksts-field">
                    <label>State</label>
                    <input
                      type="text"
                      value={searchFields.clientState}
                      onChange={e => handleFieldChange('clientState', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="State"
                    />
                  </div>
                </div>
                <div className="ksts-form-row">
                  <div className="ksts-field">
                    <label>Address</label>
                    <input
                      type="text"
                      value={searchFields.clientAddress}
                      onChange={e => handleFieldChange('clientAddress', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Address"
                    />
                  </div>
                  <div className="ksts-field">
                    <label>Website</label>
                    <input
                      type="text"
                      value={searchFields.clientWebsite}
                      onChange={e => handleFieldChange('clientWebsite', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Website"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Call Card Search Fields */}
            {isCallCardSearch && (
              <>
                <div className="ksts-form-row">
                  <div className="ksts-field">
                    <label>Event ID</label>
                    <input
                      type="text"
                      value={searchFields.eventId}
                      onChange={e => handleFieldChange('eventId', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Event ID"
                    />
                  </div>
                  <div className="ksts-field">
                    <label>Location</label>
                    <input
                      type="text"
                      value={searchFields.callLocation}
                      onChange={e => handleFieldChange('callLocation', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Location"
                    />
                  </div>
                </div>
                <div className="ksts-form-row">
                  <div className="ksts-field">
                    <label>Caller Name</label>
                    <input
                      type="text"
                      value={searchFields.callerName}
                      onChange={e => handleFieldChange('callerName', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                      placeholder="Caller name"
                    />
                  </div>
                </div>
                <div className="ksts-form-row">
                  <div className="ksts-field">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={searchFields.startDate}
                      onChange={e => handleFieldChange('startDate', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                    />
                  </div>
                  <div className="ksts-field">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={searchFields.endDate}
                      onChange={e => handleFieldChange('endDate', e.target.value)}
                      onKeyUp={handleKeyUpEnter}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Search Actions (sticky on mobile) */}
          <div className="ksts-form-actions">
            <button
              type="button"
              className="kstc-btn kstc-btn--primary"
              onClick={handleSearchClick}
              disabled={isLoading}
            >
              {isLoading ? 'Searching...' : 'Search'}
            </button>
            <button
              type="button"
              className="kstc-btn kstc-btn--secondary"
              onClick={handleResetSearch}
            >
              Clear
            </button>
          </div>
        </div>

        {/* Results Section */}
        {hasSearched && (
          <div className="ksts-results">
            <div className="ksts-results-header">
              <span>{totalRecords} result(s) found</span>
            </div>

            <div className="ksts-table-container">
              {/* Person Results Table */}
              {isPersonSearch && (
                <table className="ksts-table">
                  <thead>
                    <tr>
                      <th className="ksts-col-radio"></th>
                      <th>Last Name</th>
                      <th>First Name</th>
                      <th>DOB</th>
                      <th>DL #</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(item => (
                      <tr
                        key={item.id}
                        className={selectedItem && selectedItem.id === item.id ? 'ksts-selected' : ''}
                        onClick={() => handleSelectItem(item)}
                        onDoubleClick={() => handleDoubleClickItem(item)}
                      >
                        <td className="ksts-col-radio">
                          <input
                            type="radio"
                            checked={selectedItem && selectedItem.id === item.id}
                            onClick={e => e.stopPropagation()}
                            onChange={() => handleSelectItem(item)}
                          />
                        </td>
                        <td>{item.lastName}</td>
                        <td>{item.firstName}</td>
                        <td>{formatDate(item.dob)}</td>
                        <td>{item.driversLicenseNum}</td>
                      </tr>
                    ))}
                    {results.length === 0 && (
                      <tr>
                        <td colSpan={5} className="ksts-no-results">No results found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              {/* Vehicle Results Table */}
              {isVehicleSearch && (
                <table className="ksts-table">
                  <thead>
                    <tr>
                      <th className="ksts-col-radio"></th>
                      <th>Plate #</th>
                      <th>State</th>
                      <th>Make</th>
                      <th>Model</th>
                      <th>Year</th>
                      <th>Color</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(item => (
                      <tr
                        key={item.id}
                        className={selectedItem && selectedItem.id === item.id ? 'ksts-selected' : ''}
                        onClick={() => handleSelectItem(item)}
                        onDoubleClick={() => handleDoubleClickItem(item)}
                      >
                        <td className="ksts-col-radio">
                          <input
                            type="radio"
                            checked={selectedItem && selectedItem.id === item.id}
                            onClick={e => e.stopPropagation()}
                            onChange={() => handleSelectItem(item)}
                          />
                        </td>
                        <td>{item.plateNumber}</td>
                        <td>{item.plateState}</td>
                        <td>{item.make}</td>
                        <td>{item.model}</td>
                        <td>{item.year}</td>
                        <td>{item.color}</td>
                      </tr>
                    ))}
                    {results.length === 0 && (
                      <tr>
                        <td colSpan={7} className="ksts-no-results">No results found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              {/* Business Results Table */}
              {isBusinessSearch && (
                <table className="ksts-table">
                  <thead>
                    <tr>
                      <th className="ksts-col-radio"></th>
                      <th>Business Name</th>
                      <th>Phone</th>
                      <th className="ksts-col-hide-mobile">Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(item => (
                      <tr
                        key={item.id}
                        className={selectedItem && selectedItem.id === item.id ? 'ksts-selected' : ''}
                        onClick={() => handleSelectItem(item)}
                        onDoubleClick={() => handleDoubleClickItem(item)}
                      >
                        <td className="ksts-col-radio">
                          <input
                            type="radio"
                            checked={selectedItem && selectedItem.id === item.id}
                            onClick={e => e.stopPropagation()}
                            onChange={() => handleSelectItem(item)}
                          />
                        </td>
                        <td>{item.businessName}</td>
                        <td>{item.phone}</td>
                        <td className="ksts-col-hide-mobile">{item.locFormattedAddress}</td>
                      </tr>
                    ))}
                    {results.length === 0 && (
                      <tr>
                        <td colSpan={4} className="ksts-no-results">No results found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              {/* Client Results Table */}
              {isClientSearch && (
                <table className="ksts-table">
                  <thead>
                    <tr>
                      <th className="ksts-col-radio"></th>
                      <th>Client Name</th>
                      <th className="ksts-col-hide-mobile">Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(item => (
                      <tr
                        key={item.id}
                        className={selectedItem && selectedItem.id === item.id ? 'ksts-selected' : ''}
                        onClick={() => handleSelectItem(item)}
                        onDoubleClick={() => handleDoubleClickItem(item)}
                      >
                        <td className="ksts-col-radio">
                          <input
                            type="radio"
                            checked={selectedItem && selectedItem.id === item.id}
                            onClick={e => e.stopPropagation()}
                            onChange={() => handleSelectItem(item)}
                          />
                        </td>
                        <td>{item.name}</td>
                        <td className="ksts-col-hide-mobile">{item.locFormattedAddress}</td>
                      </tr>
                    ))}
                    {results.length === 0 && (
                      <tr>
                        <td colSpan={3} className="ksts-no-results">No results found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              {/* Call Card Results Table */}
              {isCallCardSearch && (
                <table className="ksts-table">
                  <thead>
                    <tr>
                      <th className="ksts-col-radio"></th>
                      <th>Event ID</th>
                      <th>Date/Time</th>
                      <th>Location</th>
                      <th className="ksts-col-hide-mobile">Nature of Call</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(item => (
                      <tr
                        key={item.id}
                        className={selectedItem && selectedItem.id === item.id ? 'ksts-selected' : ''}
                        onClick={() => handleSelectItem(item)}
                        onDoubleClick={() => handleDoubleClickItem(item)}
                      >
                        <td className="ksts-col-radio">
                          <input
                            type="radio"
                            checked={selectedItem && selectedItem.id === item.id}
                            onClick={e => e.stopPropagation()}
                            onChange={() => handleSelectItem(item)}
                          />
                        </td>
                        <td>{item.eventId}</td>
                        <td>{formatDateTime(item.callDateTime)}</td>
                        <td>{item.callLocFormattedAddress}</td>
                        <td className="ksts-col-hide-mobile">{formatNatureOfCall(item.natureOfCallValues)}</td>
                      </tr>
                    ))}
                    {results.length === 0 && (
                      <tr>
                        <td colSpan={5} className="ksts-no-results">No results found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {totalRecords > pageSize && (
              <div className="ksts-pagination">
                <button type="button" onClick={handlePrevPage} disabled={currentPage === 1}>Previous</button>
                <span>Page {currentPage} of {totalPages}</span>
                <button type="button" onClick={handleNextPage} disabled={currentPage >= totalPages}>Next</button>
              </div>
            )}
          </div>
        )}

        {/* Footer Actions */}
        <div className="kstc-actions">
          <button
            type="button"
            className="kstc-btn kstc-btn--secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="kstc-btn kstc-btn--secondary"
            onClick={handleViewSelected}
            disabled={!canSelect}
          >
            View
          </button>
          <button
            type="button"
            className="kstc-btn kstc-btn--primary"
            onClick={handleConfirmSelection}
            disabled={!canSelect}
          >
            Link
          </button>
        </div>
      </div>
    </div>
  );
}

(window as any).SearchModalReact = SearchModal;
